import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteAdminOrder, listAdminOrders, updateAdminOrderStatus } from '@/services/orders';
import type { Order, OrderStatus } from '@/domain/types';

export default function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    async function loadOrders() {
      try {
        setLoading(true);
        setError('');
        const nextOrders = await listAdminOrders();

        if (alive) {
          setOrders(nextOrders);
        }
      } catch (err) {
        if (alive) {
          setError(err instanceof Error ? err.message : 'Unable to load orders');
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadOrders();

    return () => {
      alive = false;
    };
  }, []);

  const filtered = orders.filter((o) => {
    if (filter !== 'all' && o.status !== filter) return false;
    if (search && !o.orderRef.toLowerCase().includes(search.toLowerCase()) && !o.productName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleStatusChange = async (id: string, status: OrderStatus) => {
    try {
      setBusyId(id);
      setError('');
      const updated = await updateAdminOrderStatus(id, status);
      setOrders((current) => current.map((order) => (order.id === id ? updated : order)));
      setSelectedOrder((current) => (current?.id === id ? updated : current));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update order');
    } finally {
      setBusyId('');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this order?')) {
      return;
    }

    try {
      setBusyId(id);
      setError('');
      await deleteAdminOrder(id);
      setOrders((current) => current.filter((order) => order.id !== id));
      setSelectedOrder(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete order');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Orders</h1>
          <p className="text-sm text-surface-500 mt-1">Manage and review all customer orders.</p>
        </div>
        <button
          onClick={() => navigate('/admin/orders/new')}
          className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors flex items-center gap-2 shrink-0">
          <i className="ri-add-line" /> New Order
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input type="text" placeholder="Search by order ref or product..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'paid', 'pending', 'failed'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filter === f ? 'bg-brand-600 text-white' : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        {error && (
          <div className="m-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Ref</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Customer</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Product</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Plan</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Amount</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Date</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-surface-500">
                    Loading orders...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-surface-500">
                    No orders found.
                  </td>
                </tr>
              )}
              {filtered.map((order) => (
                <tr key={order.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                  <td className="px-5 py-3 font-semibold text-surface-900">{order.orderRef}</td>
                  <td className="px-5 py-3 text-surface-600">{order.userId}</td>
                  <td className="px-5 py-3 text-surface-600">{order.productName}</td>
                  <td className="px-5 py-3 text-surface-500">{order.planName}</td>
                  <td className="px-5 py-3 font-semibold text-surface-900">${order.amount}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${order.status === 'paid' ? 'bg-green-50 text-green-700' : order.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-surface-500">{order.createdAt}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSelectedOrder(order)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500">
                        <i className="ri-eye-line" />
                      </button>
                      <button
                        disabled={busyId === order.id || order.status === 'refunded'}
                        onClick={() => handleStatusChange(order.id, 'refunded')}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 disabled:opacity-40 text-surface-500"
                        title="Mark refunded"
                      >
                        <i className="ri-refund-line" />
                      </button>
                      <select
                        value={order.status}
                        disabled={busyId === order.id}
                        onChange={(e) => handleStatusChange(order.id, e.target.value as OrderStatus)}
                        className="px-2 py-1 rounded-lg border border-surface-200 text-xs bg-white"
                      >
                        {['pending', 'paid', 'failed', 'refunded'].map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-xl border border-surface-200 shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-surface-900">Order {selectedOrder.orderRef}</h2>
                <p className="text-xs text-surface-500">{selectedOrder.createdAt}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="w-8 h-8 rounded-lg hover:bg-surface-100 text-surface-500">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="flex justify-between gap-4"><span className="text-surface-500">Customer</span><span className="font-medium text-surface-900">{selectedOrder.userId}</span></div>
              <div className="flex justify-between gap-4"><span className="text-surface-500">Product</span><span className="font-medium text-surface-900">{selectedOrder.productName}</span></div>
              <div className="flex justify-between gap-4"><span className="text-surface-500">Plan</span><span className="font-medium text-surface-900">{selectedOrder.planName}</span></div>
              <div className="flex justify-between gap-4"><span className="text-surface-500">Amount</span><span className="font-medium text-surface-900">${selectedOrder.amount} {selectedOrder.currency}</span></div>
              <div className="flex justify-between gap-4"><span className="text-surface-500">Status</span><span className="font-medium text-surface-900 capitalize">{selectedOrder.status}</span></div>
              <div className="flex justify-between gap-4"><span className="text-surface-500">Paid At</span><span className="font-medium text-surface-900">{selectedOrder.paidAt ?? 'Not paid'}</span></div>
            </div>
            <div className="px-5 py-4 border-t border-surface-200 flex flex-wrap justify-end gap-2">
              {(['pending', 'paid', 'failed', 'refunded'] as OrderStatus[]).map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(selectedOrder.id, status)}
                  disabled={busyId === selectedOrder.id || selectedOrder.status === status}
                  className="px-3 py-1.5 rounded-lg border border-surface-300 disabled:opacity-40 text-surface-700 text-xs font-medium capitalize"
                >
                  {status}
                </button>
              ))}
              <button
                onClick={() => handleDelete(selectedOrder.id)}
                disabled={busyId === selectedOrder.id}
                className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
